import { isMobile } from 'react-device-detect'
import DOMPurify from 'dompurify'

import { formatPhoneNumber } from '~components/internal'

export interface ProcessHtmlOptions {
  addTabIndex?: boolean
  formatPhoneNumbers?: boolean
}

export const sanitizeAndProcessLinks = (html: string, options: ProcessHtmlOptions = {}): string => {
  const { addTabIndex = false, formatPhoneNumbers = !isMobile } = options

  DOMPurify.addHook('afterSanitizeAttributes', function (node) {
    if (node.tagName === 'A') {
      const href = node.getAttribute('href') || ''

      if (href.startsWith('tel:') && formatPhoneNumbers) {
        const parent = node.parentNode
        const telNumber = href.replace('tel:', '').split(';')[0]
        const phoneNumberNode = document.createElement('span')
        try {
          phoneNumberNode.textContent = formatPhoneNumber(telNumber)
        } catch (error) {
          console.error('Error processing phone number:', error)
          phoneNumberNode.textContent = telNumber
        }
        parent?.replaceChild(phoneNumberNode, node)
        return
      }

      if (!href.startsWith('tel:')) {
        node.setAttribute('target', '_blank')
        node.setAttribute('rel', 'noopener noreferrer')
        if (addTabIndex) {
          node.setAttribute('tabindex', '0')
        }
      }
    }
  })

  try {
    const parser = new DOMParser()
    const doc = parser.parseFromString(html, 'text/html')

    const links = doc.getElementsByTagName('a')
    for (let i = 0; i < links.length; i++) {
      const link = links[i]
      if (!link.href.startsWith('tel:')) {
        link.setAttribute('target', '_blank')
        link.setAttribute('rel', 'noopener noreferrer')
        if (addTabIndex) {
          link.setAttribute('tabindex', '0')
        }
      }
    }

    return DOMPurify.sanitize(doc.body.innerHTML)
  } finally {
    DOMPurify.removeHook('afterSanitizeAttributes')
  }
}

export const markdownLinksToHtml = (text: string): string =>
  text.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>')

const BLOCK_BOUNDARY_SELECTOR =
  'br, p, div, li, tr, td, th, h1, h2, h3, h4, h5, h6, blockquote, pre, table, section, article, header, footer'

export const stripHtml = (html: string): string => {
  const doc = new DOMParser().parseFromString(html, 'text/html')
  doc.body.querySelectorAll('style, script').forEach((element) => element.remove())
  doc.body
    .querySelectorAll(BLOCK_BOUNDARY_SELECTOR)
    .forEach((element) => element.after(doc.createTextNode(' ')))
  return doc.body.textContent || ''
}
